import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type { CatalogueEntrySummary, GuardianPromptV2 } from "@/services/api/guardiansv2/types";
import { ReadableDataPanel } from "@/components/ui/readable-content";
import { buildRenderedPromptPreview } from "./promptPreviewContent";
import { PROMPT_EXAMPLE_CONTEXT } from "./promptExampleContext";
import {
  SCREENING_NAV_KEY,
  PROMPT_NAV_SECTIONS,
  SYNTHESIS_PERSONA,
  auditTypeHeading,
  defaultPromptNavKey,
  findScreeningPrompt,
  groupAuditPrompts,
  parsePromptNavKey,
  personaLabel,
  promptNavKey,
} from "./promptsPanelNav";
import { catalogueEntryReactKey } from "../rules/catalogueEntryKey";

function collectCatalogueEntries(
  groups: Awaited<ReturnType<typeof GuardiansV2API.listRulesCatalogue>>["groups"],
  ungrouped: CatalogueEntrySummary[]
): CatalogueEntrySummary[] {
  const seen = new Set<string>();
  const out: CatalogueEntrySummary[] = [];
  const push = (entry: CatalogueEntrySummary) => {
    if (seen.has(entry.key)) return;
    seen.add(entry.key);
    out.push(entry);
  };
  for (const group of groups) {
    if (group.mission_scope) push(group.mission_scope);
    for (const entry of group.entries) push(entry);
  }
  for (const entry of ungrouped) push(entry);
  return out.sort((a, b) => a.display_name.localeCompare(b.display_name));
}

function NavButton({
  label,
  active,
  onClick,
  testId,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded px-2 py-1.5 text-left ${
        active
          ? "bg-indigo-500/30 text-indigo-100"
          : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-200"
      }`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function EntityPersonaSpoiler({
  label,
  items,
  selectedKey,
  onSelect,
  auditType,
}: {
  label: string;
  items: GuardianPromptV2[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  auditType: string;
}) {
  const activeInside = items.some((p) => promptNavKey(p.audit_type, p.persona) === selectedKey);
  return (
    <details
      className="group/prompt-entity"
      open={activeInside}
      data-testid={`prompt-entity-${auditType}`}
    >
      <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-lg px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-400 [&::-webkit-details-marker]:hidden">
        <FontAwesomeIcon
          icon={faChevronRight}
          className="size-2.5 shrink-0 transition-transform group-open/prompt-entity:rotate-90"
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate">{label}</span>
      </summary>
      <ul className="mt-0.5 space-y-0.5 pl-1">
        {items.map((p) => {
          const key = promptNavKey(p.audit_type, p.persona);
          return (
            <li key={key}>
              <NavButton
                label={personaLabel(p.persona)}
                active={selectedKey === key}
                onClick={() => onSelect(key)}
                testId={`prompt-nav-${p.audit_type}-${p.persona}`}
              />
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/** Read-only view of DB-backed Guardians prompt templates with example rendering. */
export function PromptsPanel() {
  const [prompts, setPrompts] = useState<GuardianPromptV2[]>([]);
  const [catalogueEntries, setCatalogueEntries] = useState<CatalogueEntrySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [screeningCatalogueKey, setScreeningCatalogueKey] = useState<string | null>(null);
  const [screeningContext, setScreeningContext] = useState<Record<string, string> | null>(null);
  const [screeningContextLoading, setScreeningContextLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([GuardiansV2API.listPrompts(), GuardiansV2API.listRulesCatalogue()])
      .then(([promptRes, catalogueRes]) => {
        setPrompts(promptRes.prompts);
        const entries = collectCatalogueEntries(catalogueRes.groups, catalogueRes.ungrouped);
        setCatalogueEntries(entries);
        setScreeningCatalogueKey(entries[0]?.key ?? null);
        setSelectedKey(defaultPromptNavKey(promptRes.prompts));
      })
      .catch(() => setError("Failed to load prompts"))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => groupAuditPrompts(prompts), [prompts]);
  const screeningPrompt = useMemo(() => findScreeningPrompt(prompts), [prompts]);

  const selected = useMemo(() => {
    const parsed = selectedKey ? parsePromptNavKey(selectedKey) : null;
    if (!parsed) return null;
    if (parsed.auditType === "_screening") return screeningPrompt;
    return (
      prompts.find((p) => p.audit_type === parsed.auditType && p.persona === parsed.persona) ?? null
    );
  }, [prompts, screeningPrompt, selectedKey]);

  const isScreening = selectedKey === SCREENING_NAV_KEY;

  useEffect(() => {
    if (!isScreening || !screeningCatalogueKey) {
      setScreeningContext(null);
      return;
    }
    let cancelled = false;
    setScreeningContextLoading(true);
    GuardiansV2API.getScreeningPromptPreview(screeningCatalogueKey)
      .then((res) => {
        if (!cancelled) setScreeningContext(res.context);
      })
      .catch(() => {
        if (!cancelled) setScreeningContext(null);
      })
      .finally(() => {
        if (!cancelled) setScreeningContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isScreening, screeningCatalogueKey]);

  const preview = useMemo(() => {
    if (!selected) return null;
    const context =
      isScreening && screeningContext ? screeningContext : PROMPT_EXAMPLE_CONTEXT;
    return buildRenderedPromptPreview(
      selected.system_prompt,
      selected.user_prompt_template,
      context
    );
  }, [isScreening, screeningContext, selected]);

  if (loading) {
    return (
      <p className="text-sm text-slate-400" data-testid="prompts-panel-loading">
        Loading prompts…
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-300" data-testid="prompts-panel-error">
        {error}
      </p>
    );
  }

  if (prompts.length === 0) {
    return (
      <p className="text-sm text-slate-500" data-testid="prompts-panel-empty">
        No active prompts in the database. Run the seed script to populate templates.
      </p>
    );
  }

  return (
    <div className="flex min-h-[360px] flex-col gap-4" data-testid="prompts-panel">
      <p className="text-sm text-slate-400">
        Active templates from <code className="text-slate-300">guardiansv2.prompts</code>.
        User prompts are shown with example placeholder data
        {isScreening ? " from a recent completed task of the selected type" : ""}.
      </p>
      <div className="flex min-h-0 flex-1 gap-3">
        <nav
          className="w-44 shrink-0 space-y-3 overflow-y-auto pr-1 text-xs"
          aria-label="Prompt templates"
        >
          {PROMPT_NAV_SECTIONS.map((section, index) => {
            if (section.kind === "separator") {
              return (
                <hr
                  key={`sep-${index}`}
                  className="border-white/10"
                  data-testid="prompt-nav-separator"
                />
              );
            }
            if (section.kind === "rules_check") {
              if (!screeningPrompt) return null;
              return (
                <div key="rules_check">
                  <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    {section.label}
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      <NavButton
                        label="Ace"
                        active={selectedKey === SCREENING_NAV_KEY}
                        onClick={() => setSelectedKey(SCREENING_NAV_KEY)}
                        testId="prompt-nav-rules_check-ace"
                      />
                    </li>
                  </ul>
                </div>
              );
            }
            const items = grouped.get(section.auditType);
            if (!items?.length) return null;
            return (
              <EntityPersonaSpoiler
                key={section.auditType}
                label={section.label}
                items={items}
                selectedKey={selectedKey}
                onSelect={setSelectedKey}
                auditType={section.auditType}
              />
            );
          })}
        </nav>
        {selected && preview ? (
          <div className="min-w-0 flex-1 space-y-3 overflow-y-auto">
            <div>
              <h3 className="text-sm font-medium text-slate-100">
                {auditTypeHeading(selected.audit_type)} · {personaLabel(selected.persona)}
              </h3>
              <div className="mt-1 flex items-start justify-between gap-3">
                <p className="min-w-0 text-xs text-slate-500">
                  Version {selected.version ?? "—"}
                  {selected.description ? ` · ${selected.description}` : ""}
                  {isScreening && screeningContextLoading ? " · Loading context…" : ""}
                </p>
                {isScreening && catalogueEntries.length > 0 ? (
                  <select
                    className="max-w-[min(100%,14rem)] shrink-0 rounded border border-white/10 bg-slate-900 px-2 py-1 text-xs text-slate-200"
                    value={screeningCatalogueKey ?? ""}
                    onChange={(e) => setScreeningCatalogueKey(e.target.value)}
                    aria-label="Task type for prompt context"
                    data-testid="prompt-screening-catalogue"
                  >
                    {catalogueEntries.map((entry, index) => (
                      <option key={catalogueEntryReactKey(entry, index)} value={entry.key}>
                        {entry.display_name}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            </div>
            <ReadableDataPanel
              title={`${selected.audit_type} / ${selected.persona === SYNTHESIS_PERSONA ? "synthesis" : selected.persona}`}
              content={preview.content}
              maxHeightClass="max-h-[min(52vh,28rem)]"
              data-testid="prompt-preview-panel"
            />
          </div>
        ) : (
          <p className="text-sm text-slate-500">Select a prompt to preview</p>
        )}
      </div>
    </div>
  );
}
